/**
 * Embedded Map for Network Graph Side Panel
 * Shows location data from proximity-based link analysis
 * Filters by selected nodes
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import Map, { NavigationControl, ScaleControl, MapRef, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Typography, Paper, Stack } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CircleIcon from '@mui/icons-material/Circle';
import type { MapLayerMouseEvent } from 'react-map-gl';
import MapStyleControl, { MapStyleType } from '../cop/MapStyleControl';
import { useTracerState } from '../../contexts/TracerContext';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
// US center - will be overridden by fitBounds or first data point
const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283];
const DEFAULT_ZOOM = 4;

interface Node {
  id: string;
  type?: string;
  label?: string;
  [key: string]: any;
}

interface NetworkGraphMapProps {
  selectedNodes: Set<string>;
  nodes: Node[];
}

const NetworkGraphMap: React.FC<NetworkGraphMapProps> = ({ selectedNodes, nodes }) => {
  const { state: tracerState } = useTracerState();
  const mapRef = useRef<MapRef | null>(null);
  const [mapStyle, setMapStyle] = useState<MapStyleType>('dark');

  // Convert map style type to Mapbox style URL
  const getMapboxStyle = (style: MapStyleType): string => {
    const styles: Record<MapStyleType, string> = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
      dark: 'mapbox://styles/mapbox/dark-v11',
      light: 'mapbox://styles/mapbox/light-v11',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12',
    };
    return styles[style] || styles.dark;
  };

  // Extract location markers from GPS data points AND location nodes, convert to GeoJSON
  const geojsonData = useMemo(() => {
    const features: any[] = [];
    const dataPoints = tracerState.dataPoints || [];

    // If nodes are selected, only show data points for those devices/locations
    const selectedIds = selectedNodes.size > 0 
      ? Array.from(selectedNodes)
      : nodes.map(n => n.id);

    // First, add location nodes from the graph (for Shared Locations view)
    nodes.forEach((node: any) => {
      // Check if this is a location node with coordinates
      if (node.type === 'location' && node.coordinates) {
        const coords = node.coordinates;
        if (!coords || !Array.isArray(coords) || coords.length < 2) return;
        
        const lat = coords[0];
        const lng = coords[1];
        
        if (typeof lat !== 'number' || typeof lng !== 'number') return;
        if (isNaN(lat) || isNaN(lng)) return;

        // Only show if selected or no selection
        if (selectedNodes.size > 0 && !selectedNodes.has(node.id)) return;

        features.push({
          type: 'Feature',
          properties: {
            id: node.id,
            type: 'location',
            label: node.label || node.id,
            visitCount: node.visitCount || 0,
            deviceCount: node.deviceCount || 0,
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat],  // GeoJSON is [lng, lat]
          },
        });
      }
    });

    // Then, add GPS data points (for Proximity view or general data)
    dataPoints.forEach((point: any, index: number) => {
      const deviceId = point.deviceId || point.studentId || '';
      
      // Check if this point's device is in the graph
      if (!selectedIds.includes(deviceId)) return;
      
      // Extract coordinates
      const coords = point.coordinates;
      if (!coords || !Array.isArray(coords) || coords.length < 2) return;
      
      const lat = coords[0];
      const lng = coords[1];
      
      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      if (isNaN(lat) || isNaN(lng)) return;

      features.push({
        type: 'Feature',
        properties: {
          id: `point-${deviceId}-${index}`,
          type: 'proximity',  // Mark as proximity-based data
          device_id: deviceId,
          timestamp: point.timestamp,
          accuracy: point.accuracy,
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],  // GeoJSON is [lng, lat]
        },
      });
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [tracerState.dataPoints, selectedNodes, nodes]);

  // Use purple for proximity-based GPS data
  const clusterColor = '#9333ea'; // Purple for GPS/proximity data

  // Helper: valid latitude/longitude for Mapbox
  const isValidLat = (v: number) => Number.isFinite(v) && v >= -90 && v <= 90;
  const isValidLng = (v: number) => Number.isFinite(v) && v >= -180 && v <= 180;

  // Calculate map bounds to fit all markers
  const bounds = useMemo(() => {
    if (geojsonData.features.length === 0) return null;

    const coords = geojsonData.features.map((f: any) => f.geometry.coordinates);
    const lngs = coords.map((c: number[]) => c[0]);
    const lats = coords.map((c: number[]) => c[1]);

    return {
      minLng: Math.min(...lngs),
      minLat: Math.min(...lats),
      maxLng: Math.max(...lngs),
      maxLat: Math.max(...lats),
    };
  }, [geojsonData]);

  // Fit map to bounds when markers change - only if bounds are valid
  useEffect(() => {
    if (!mapRef.current || !bounds || geojsonData.features.length <= 1) return;

    const { minLng, minLat, maxLng, maxLat } = bounds;
    if (
      isValidLng(minLng) &&
      isValidLat(minLat) &&
      isValidLng(maxLng) &&
      isValidLat(maxLat)
    ) {
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 1000 }
      );
    }
  }, [bounds, geojsonData.features.length]);
  
  // Handle cluster click - zoom into cluster
  const onClusterClick = (event: MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    if (!feature || !mapRef.current) return;

    const clusterId = feature.properties?.cluster_id;
    const source = mapRef.current.getSource('dwells') as any;

    if (source && source.getClusterExpansionZoom) {
      source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (err || !feature.geometry || feature.geometry.type !== 'Point') return;

        mapRef.current?.easeTo({
          center: feature.geometry.coordinates as [number, number],
          zoom: zoom,
          duration: 500,
        });
      });
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {geojsonData.features.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              textAlign: 'center'
            }}
          >
            <LocationOnIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              No location data available.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              In Shared Locations view, location nodes will appear here.
              In Proximity view, load GPS data from the OSINT Explorer page.
            </Typography>
          </Box>
        ) : (
          <Map
            ref={mapRef}
            initialViewState={{
              longitude: geojsonData.features[0]?.geometry.coordinates[0] || DEFAULT_CENTER[0],
              latitude: geojsonData.features[0]?.geometry.coordinates[1] || DEFAULT_CENTER[1],
              zoom: geojsonData.features.length === 1 ? 14 : DEFAULT_ZOOM,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={getMapboxStyle(mapStyle)}
            mapboxAccessToken={MAPBOX_TOKEN}
            interactiveLayerIds={['clusters', 'unclustered-point']}
            onClick={onClusterClick}
          >
            {/* Compact Navigation Control */}
            <NavigationControl position="top-right" style={{ transform: 'scale(0.8)', transformOrigin: 'top right' }} />
            <ScaleControl position="bottom-right" style={{ transform: 'scale(0.8)', transformOrigin: 'bottom right' }} />

            {/* Compact Map Style Control - wrapped to override default size */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 1000,
                '& > div': {
                  position: 'static !important',
                  margin: 0
                },
                '& button': {
                  width: 32,
                  height: 32,
                  '& svg': {
                    fontSize: '1.2rem'
                  }
                }
              }}
            >
              <MapStyleControl
                selectedMapStyle={mapStyle}
                onMapStyleChange={setMapStyle}
                position="top-left"
              />
            </Box>

            {/* Legend */}
            <Paper
              elevation={2}
              sx={{
                position: 'absolute',
                bottom: 48,
                left: 8,
                zIndex: 1000,
                p: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
              }}
            >
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Map Legend
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CircleIcon sx={{ fontSize: 12, color: '#ff0066' }} />
                  <Typography variant="caption">Shared Locations</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CircleIcon sx={{ fontSize: 12, color: '#9333ea' }} />
                  <Typography variant="caption">Device GPS Points</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {geojsonData.features.filter((f: any) => f.properties.type === 'location').length} locations, {' '}
                  {geojsonData.features.filter((f: any) => f.properties.type === 'proximity').length} GPS points
                </Typography>
              </Stack>
            </Paper>

            {/* Data Source with clustering */}
            <Source
              id="dwells"
              type="geojson"
              data={geojsonData as any}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
            >
              {/* Cluster circles */}
              <Layer
                id="clusters"
                type="circle"
                source="dwells"
                filter={['has', 'point_count']}
                paint={{
                  'circle-color': clusterColor,  // Dynamic: matches type if filtered, grey if mixed
                  'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    18,   // 18px for small clusters
                    10,
                    25,   // 25px for medium clusters
                    30,
                    35    // 35px for large clusters
                  ],
                  'circle-opacity': 0.8,
                  'circle-stroke-width': 3,
                  'circle-stroke-color': '#fff'
                }}
              />

              {/* Cluster count labels */}
              <Layer
                id="cluster-count"
                type="symbol"
                source="dwells"
                filter={['has', 'point_count']}
                layout={{
                  'text-field': '{point_count_abbreviated}',
                  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                  'text-size': 14
                }}
                paint={{
                  'text-color': '#ffffff',
                  'text-halo-color': '#000000',
                  'text-halo-width': 1
                }}
              />

              {/* Individual unclustered points - color by type */}
              <Layer
                id="unclustered-point"
                type="circle"
                source="dwells"
                filter={['!', ['has', 'point_count']]}
                paint={{
                  // Pink for location nodes, purple for GPS points
                  'circle-color': [
                    'case',
                    ['==', ['get', 'type'], 'location'], '#ff0066',  // Pink for shared locations
                    '#9333ea'  // Purple for GPS points
                  ],
                  // Larger radius for location nodes
                  'circle-radius': [
                    'case',
                    ['==', ['get', 'type'], 'location'], 14,  // Larger for locations
                    8  // Smaller for GPS points
                  ],
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#fff'
                }}
              />

              {/* Labels for location nodes */}
              <Layer
                id="location-labels"
                type="symbol"
                source="dwells"
                filter={['==', ['get', 'type'], 'location']}
                layout={{
                  'text-field': ['get', 'label'],
                  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                  'text-size': 11,
                  'text-offset': [0, 1.5],
                  'text-anchor': 'top',
                  'text-max-width': 10
                }}
                paint={{
                  'text-color': '#ffffff',
                  'text-halo-color': '#000000',
                  'text-halo-width': 1
                }}
              />
            </Source>
          </Map>
        )}
      </Box>
    </Box>
  );
};

export default NetworkGraphMap;

